/**
 * PasswordInput — Input variant with a built-in eye/eye-off visibility
 * toggle. Default state is `type="password"` (hidden); the toggle button
 * flips to `type="text"`.
 *
 * Design contract:
 *   - Pure UI wrapper. No auth state, no value mutation, no submit
 *     interception. Authentication logic, session handling, validation
 *     attributes, and styling all pass through untouched.
 *   - Toggle is keyboard-reachable (button), screen-reader friendly
 *     (aria-label + aria-pressed), and the click does NOT steal focus
 *     from the input (button uses tabIndex={-1} + onMouseDown
 *     preventDefault — matches the dominant a11y pattern for show/hide
 *     toggles next to inputs).
 *   - Tap target is 40x40 on mobile (h-10/w-10 inside an absolutely
 *     positioned wrapper) and never overlaps the typing area.
 *   - Forwards refs so existing form libraries that target the underlying
 *     input keep working.
 */
import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./input";
import { cn } from "@/lib/utils";

export const PasswordInput = React.forwardRef(
  ({ className, "data-testid": testId, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    const id = props.id;
    const toggleTestId = testId ? `${testId}-toggle` : "password-toggle";
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? "text" : "password"}
          // Reserve room for the toggle button so long values don't slide
          // under the icon.
          className={cn("pr-10", className)}
          data-testid={testId}
          {...props}
        />
        <button
          type="button"
          // Avoid stealing focus from the input on tap/click; this keeps
          // the caret position stable and respects the user's typing
          // flow on mobile and desktop.
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          aria-controls={id}
          className="absolute inset-y-0 right-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-r-md"
          data-testid={toggleTestId}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
